import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, SegmentedControl, Spinner } from '../../../components/ui';
import {
  stockMovementService,
  type CatalogMaterial,
  type MovementOptions,
  type MovementType,
} from '../../../services/stockMovementService';
import type { SiteWithLocations } from '../types';
import {
  COUNT_METHOD_OPTIONS,
  EMPTY_FORM,
  MOVEMENT_TABS,
  TYPE_SPEC,
  arrivalBins,
  buildPayload,
  countableBins,
  describeMovement,
  expectedVsCounted,
  flattenBins,
  formatCost,
  formatQuantity,
  overdrawWarning,
  siteMaterialChoices,
  sourceBins,
  validateAdjust,
  type AdjustForm,
  type MaterialChoice,
  type SiteBin,
} from '../utils/adjustInventory';
import { binMaterialLabel } from '../utils/siteDrillThrough';
import MaterialPicker from './MaterialPicker';

interface AdjustInventoryModalProps {
  show: boolean;
  site: SiteWithLocations;
  /** The tab to open on. */
  initialType: MovementType;
  /** Pre-selects this bin (from a row's Adjust action) wherever it is eligible. */
  initialBinId?: string | null;
  /** materials:cost_manage. Without it the unit cost on a receipt is read-only. */
  canManageCost: boolean;
  onHide: () => void;
  /** Called after the server accepted the movement; the page reloads the site and says so. */
  onSaved: (message: string) => void;
}

type OptionsState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: MovementOptions };

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

const Required: React.FC = () => (
  <span className="tw-text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

/** The shared label style, for places a <label for> cannot be used (a listbox is not labelable). */
const LABEL_CLASS = 'tw-mb-1 tw-font-medium tw-text-content-secondary';

function catalogChoice(material: CatalogMaterial, onHand: number | null): MaterialChoice {
  const code = material.code?.trim();
  const description = material.description?.trim();
  return {
    id: material.id,
    label: code && description && code !== description ? `${code} (${description})` : code || description || 'Unknown material',
    group: material.type?.trim() || 'Other',
    unit: material.unitOfMeasure,
    onHand,
  };
}

const AdjustInventoryModal: React.FC<AdjustInventoryModalProps> = ({
  show,
  site,
  initialType,
  initialBinId = null,
  canManageCost,
  onHide,
  onSaved,
}) => {
  const bins = useMemo(() => flattenBins(site), [site]);
  const [type, setType] = useState<MovementType>(initialType);
  const [form, setForm] = useState<AdjustForm>(EMPTY_FORM);
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [options, setOptions] = useState<OptionsState>({ status: 'loading' });
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState<CatalogMaterial[]>([]);
  const [searching, setSearching] = useState(false);
  /** Default rates by material id, from the catalogue. Not every material has one. */
  const [rates, setRates] = useState<Record<string, number | null>>({});
  const [costTouched, setCostTouched] = useState(false);

  const spec = TYPE_SPEC[type];
  const findRow = useCallback((id: string): SiteBin | undefined => bins.find(({ bin }) => bin.id === id), [bins]);
  const fromRow = findRow(form.fromBin);
  const toRow = findRow(form.toBin);

  /** Can stock for this type be taken from / put into this bin right now? */
  const eligibleFor = useCallback(
    (forType: MovementType, row: SiteBin): 'from' | 'to' | null => {
      const forSpec = TYPE_SPEC[forType];
      if (forSpec.fromLabel) {
        if (forType === 'COUNT') return 'from';
        return sourceBins([row], '').length ? 'from' : null;
      }
      if (forSpec.toLabel) return 'to';
      return null;
    },
    [],
  );

  /** Pre-select a bin for a type, only where that bin makes sense for it. */
  const withBin = useCallback(
    (base: AdjustForm, forType: MovementType, binId: string | null): AdjustForm => {
      const row = binId ? findRow(binId) : undefined;
      if (!row) return base;
      const side = eligibleFor(forType, row);
      if (!side) return base;
      return {
        ...base,
        fromBin: side === 'from' ? row.bin.id : base.fromBin,
        toBin: side === 'to' ? row.bin.id : base.toBin,
        material: row.bin.material ? row.bin.material.id : base.material,
      };
    },
    [eligibleFor, findRow],
  );

  const loadSupport = useCallback(() => {
    let cancelled = false;
    setOptions({ status: 'loading' });
    stockMovementService
      .getOptions(site.id)
      .then((data) => {
        if (!cancelled) setOptions({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setOptions({
          status: 'error',
          message: error instanceof Error ? error.message : 'Could not load tasks and vendors.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [site.id]);

  // Open fresh: the form must not remember the last movement, or a second adjustment would
  // start pre-filled with the first one's quantity and reason.
  useEffect(() => {
    if (!show) return undefined;
    setType(initialType);
    setForm(withBin(EMPTY_FORM, initialType, initialBinId));
    setAttempted(false);
    setSaving(false);
    setSaveError(null);
    setQuery('');
    setCatalog([]);
    setCostTouched(false);
    return loadSupport();
    // Re-run only when the dialog opens; later changes to `bins` must not wipe what is typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Default cost per material, for materials the site already holds. Fetched once per open.
  useEffect(() => {
    if (!show) return undefined;
    const ids = Array.from(new Set(bins.flatMap(({ bin }) => (bin.material ? [bin.material.id] : []))));
    if (ids.length === 0) return undefined;
    let cancelled = false;
    stockMovementService
      .searchMaterials('', site.id, ids)
      .then((found) => {
        if (cancelled) return;
        setRates((current) => ({
          ...current,
          ...Object.fromEntries(found.map((material) => [material.id, material.defaultUnitCost])),
        }));
      })
      .catch(() => {
        // Not fatal: without a rate the cost field simply has no default to offer.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, site.id]);

  const searchMode = type === 'RECEIPT' || (type === 'COUNT' && !!fromRow && !fromRow.bin.material);

  // Catalogue search, debounced. Below the minimum length the list falls back to what the
  // site holds, so a freshly opened Receipt tab is not empty.
  useEffect(() => {
    if (!show || !searchMode) return undefined;
    const needle = query.trim();
    if (needle.length < MIN_SEARCH_LENGTH) {
      setCatalog([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      stockMovementService
        .searchMaterials(needle, site.id)
        .then((found) => {
          if (cancelled) return;
          setCatalog(found);
          setRates((current) => ({
            ...current,
            ...Object.fromEntries(found.map((material) => [material.id, material.defaultUnitCost])),
          }));
        })
        .catch(() => {
          if (!cancelled) setCatalog([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [show, searchMode, query, site.id]);

  const siteTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const { bin } of bins) {
      if (bin.material) totals.set(bin.material.id, (totals.get(bin.material.id) ?? 0) + bin.quantity);
    }
    return totals;
  }, [bins]);

  // Which materials the picker offers, narrowest first: a chosen bin pins its own material.
  const choices: MaterialChoice[] = useMemo(() => {
    if (searchMode) {
      if (query.trim().length >= MIN_SEARCH_LENGTH) {
        return catalog.map((material) => catalogChoice(material, siteTotals.get(material.id) ?? null));
      }
      return siteMaterialChoices(bins, { inStockOnly: false });
    }
    const anchor = spec.fromLabel ? fromRow : toRow;
    if (anchor?.bin.material) {
      const material = anchor.bin.material;
      return [
        {
          id: material.id,
          label: binMaterialLabel(anchor.bin) ?? 'Unknown material',
          group: material.type?.trim() || 'Other',
          unit: material.unitOfMeasure,
          onHand: anchor.bin.quantity,
        },
      ];
    }
    return siteMaterialChoices(bins, { inStockOnly: spec.drawsDown });
  }, [bins, catalog, fromRow, toRow, query, searchMode, siteTotals, spec.drawsDown, spec.fromLabel]);

  const selectedUnit = useMemo(() => {
    const held = bins.find(({ bin }) => bin.material?.id === form.material)?.bin.material;
    if (held) return held.unitOfMeasure;
    return catalog.find((material) => material.id === form.material)?.unitOfMeasure ?? null;
  }, [bins, catalog, form.material]);

  const fromOptions = useMemo(
    () => (type === 'COUNT' ? countableBins(bins, form.material) : sourceBins(bins, form.material)),
    [bins, form.material, type],
  );
  const toOptions = useMemo(
    () =>
      arrivalBins(bins, form.material, {
        allowEmptyReassign: type === 'RECEIPT',
        excludeBinId: type === 'TRANSFER' ? form.fromBin : undefined,
      }),
    [bins, form.fromBin, form.material, type],
  );

  /** The cost the server will accept from someone who cannot set one. */
  const costOnFile: number | null = useMemo(() => {
    if (type !== 'RECEIPT') return null;
    const compatible = !toRow?.bin.material || toRow.bin.material.id === form.material;
    if (toRow && compatible && toRow.bin.unitCost !== null) return toRow.bin.unitCost;
    return rates[form.material] ?? null;
  }, [form.material, rates, toRow, type]);

  // Keep the cost field on the cost on file until someone who may change it does so.
  useEffect(() => {
    if (type !== 'RECEIPT' || costTouched) return;
    const next = costOnFile === null ? '' : String(costOnFile);
    setForm((current) => (current.unitCost === next ? current : { ...current, unitCost: next }));
  }, [costOnFile, costTouched, type]);

  const errors = validateAdjust(type, form, { bins, canManageCost, costOnFile });
  const overdraw = overdrawWarning(type, form, bins);
  const shown = attempted ? errors : {};

  const setField = <K extends keyof AdjustForm>(key: K, value: AdjustForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const changeType = (next: MovementType) => {
    if (next === type) return;
    const carry = form.fromBin || form.toBin || initialBinId;
    setType(next);
    setForm(withBin({ ...EMPTY_FORM, reason: form.reason }, next, carry));
    setAttempted(false);
    setSaveError(null);
    setQuery('');
    setCostTouched(false);
  };

  const chooseMaterial = (materialId: string) => {
    setForm((current) => {
      const next: AdjustForm = { ...current, material: materialId };
      const from = findRow(current.fromBin);
      if (from?.bin.material && from.bin.material.id !== materialId) next.fromBin = '';
      const to = findRow(current.toBin);
      if (to?.bin.material && to.bin.material.id !== materialId && !(type === 'RECEIPT' && to.bin.quantity === 0)) {
        next.toBin = '';
      }
      // One bin holds it: no reason to make the user pick the only possible answer.
      if (spec.fromLabel && !next.fromBin) {
        const only = (type === 'COUNT' ? countableBins(bins, materialId) : sourceBins(bins, materialId)).filter(
          ({ bin }) => bin.material?.id === materialId,
        );
        if (only.length === 1) next.fromBin = only[0].bin.id;
      }
      return next;
    });
  };

  const chooseFromBin = (binId: string) => {
    setForm((current) => {
      const row = findRow(binId);
      const next: AdjustForm = { ...current, fromBin: binId };
      if (row?.bin.material) next.material = row.bin.material.id;
      else if (type !== 'COUNT') next.material = '';
      if (next.toBin === binId) next.toBin = '';
      return next;
    });
  };

  const chooseToBin = (binId: string) => {
    setForm((current) => {
      const row = findRow(binId);
      const next: AdjustForm = { ...current, toBin: binId };
      // Fill the material from the bin, but never override one already chosen: a receipt may
      // deliberately put something new into an empty bin.
      if (row?.bin.material && !current.material) next.material = row.bin.material.id;
      return next;
    });
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (Object.keys(errors).length > 0 || saving) return;

    setSaving(true);
    setSaveError(null);
    try {
      const movement = await stockMovementService.createMovement(buildPayload(type, form, bins));
      const notes = [
        ...(movement.warnings ?? []).map((warning) => warning.message),
        ...(movement.costCarried ? ['The origin cost was carried to the destination; a materials controller will confirm it.'] : []),
      ];
      onSaved(
        [describeMovement(movement.type, movement.quantity, movement.unitOfMeasure ?? selectedUnit, movement.expectedQuantity), ...notes].join(' ')
      );
      onHide();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to record the movement');
    } finally {
      setSaving(false);
    }
  };

  const optionsReady = options.status === 'ready';
  const taskOptions = optionsReady ? options.data.tasks : [];
  const vendorOptions = optionsReady ? options.data.vendors : [];
  const waiting = options.status === 'loading' ? 'Loading…' : null;

  const binLabel = (row: SiteBin, withStock: boolean) =>
    withStock && row.bin.material ? `${row.label} (${formatQuantity(row.bin.quantity, row.bin.material.unitOfMeasure)})` : row.label;

  const unitHint = selectedUnit ? <Form.Text>{selectedUnit}</Form.Text> : null;

  const fromField = spec.fromLabel ? (
    <Form.Group controlId="adjust-from-bin" className="tw-mb-0">
      <Form.Label>
        {spec.fromLabel}
        <Required />
      </Form.Label>
      <Form.Select
        value={form.fromBin}
        isInvalid={!!shown.fromBin}
        onChange={(event) => chooseFromBin(event.target.value)}
      >
        <option value="">Choose a location…</option>
        {fromOptions.map((row) => (
          <option key={row.bin.id} value={row.bin.id}>
            {binLabel(row, type !== 'COUNT')}
          </option>
        ))}
      </Form.Select>
      {shown.fromBin ? <Form.Control.Feedback type="invalid">{shown.fromBin}</Form.Control.Feedback> : null}
    </Form.Group>
  ) : null;

  const toField = spec.toLabel ? (
    <Form.Group controlId="adjust-to-bin" className="tw-mb-0">
      <Form.Label>
        {spec.toLabel}
        <Required />
      </Form.Label>
      <Form.Select
        value={form.toBin}
        isInvalid={!!shown.toBin}
        onChange={(event) => chooseToBin(event.target.value)}
      >
        <option value="">Choose a location…</option>
        {toOptions.map((row) => (
          <option key={row.bin.id} value={row.bin.id}>
            {binLabel(row, false)}
          </option>
        ))}
      </Form.Select>
      {shown.toBin ? <Form.Control.Feedback type="invalid">{shown.toBin}</Form.Control.Feedback> : null}
    </Form.Group>
  ) : null;

  const quantityField = spec.quantity ? (
    <Form.Group controlId="adjust-quantity" className="tw-mb-0">
      <Form.Label>
        Quantity
        <Required />
      </Form.Label>
      <Form.Control
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        value={form.quantity}
        isInvalid={!!shown.quantity}
        onChange={(event) => setField('quantity', event.target.value)}
      />
      {shown.quantity ? (
        <Form.Control.Feedback type="invalid">{shown.quantity}</Form.Control.Feedback>
      ) : overdraw ? (
        <Form.Text className="tw-text-warning-text" role="status">
          {overdraw}
        </Form.Text>
      ) : (
        unitHint
      )}
    </Form.Group>
  ) : null;

  const taskField = spec.task ? (
    <Form.Group controlId="adjust-task" className="tw-mb-0">
      <Form.Label>
        {spec.task === 'required' ? (
          <>
            Task
            <Required />
          </>
        ) : (
          'Task (optional)'
        )}
      </Form.Label>
      <Form.Select
        value={form.task}
        isInvalid={!!shown.task}
        disabled={!optionsReady}
        onChange={(event) => setField('task', event.target.value)}
      >
        <option value="">{waiting ?? (spec.task === 'required' ? 'Choose a task…' : 'Not tied to a task')}</option>
        {taskOptions.map((task) => (
          <option key={task.id} value={task.id}>
            {task.label}
          </option>
        ))}
      </Form.Select>
      {shown.task ? <Form.Control.Feedback type="invalid">{shown.task}</Form.Control.Feedback> : null}
    </Form.Group>
  ) : null;

  const vendorField = spec.vendor ? (
    <Form.Group controlId="adjust-vendor" className="tw-mb-0">
      <Form.Label>
        Vendor
        <Required />
      </Form.Label>
      <Form.Select
        value={form.vendor}
        isInvalid={!!shown.vendor}
        disabled={!optionsReady}
        onChange={(event) => setField('vendor', event.target.value)}
      >
        <option value="">{waiting ?? 'Choose a vendor…'}</option>
        {vendorOptions.map((vendor) => (
          <option key={vendor.id} value={vendor.id}>
            {vendor.name}
          </option>
        ))}
      </Form.Select>
      {shown.vendor ? <Form.Control.Feedback type="invalid">{shown.vendor}</Form.Control.Feedback> : null}
    </Form.Group>
  ) : null;

  const costField = spec.unitCost ? (
    <Form.Group controlId="adjust-unit-cost" className="tw-mb-0">
      <Form.Label>
        Unit cost
        <Required />
      </Form.Label>
      <Form.Control
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        value={form.unitCost}
        readOnly={!canManageCost}
        isInvalid={!!shown.unitCost}
        onChange={(event) => {
          setCostTouched(true);
          setField('unitCost', event.target.value);
        }}
      />
      {shown.unitCost ? (
        <Form.Control.Feedback type="invalid">{shown.unitCost}</Form.Control.Feedback>
      ) : (
        <Form.Text>
          {canManageCost
            ? 'Entering a different cost changes this bin’s cost.'
            : costOnFile !== null
              ? 'Set by the materials controller.'
              : 'No cost is on file. Ask a materials controller to set one.'}
        </Form.Text>
      )}
    </Form.Group>
  ) : null;

  const costPanel = spec.costPanel ? (
    <div
      data-testid="transfer-cost-panel"
      className="tw-grid tw-gap-4 tw-rounded-lg tw-border tw-border-line tw-bg-surface-alt tw-p-3 sm:tw-grid-cols-2"
    >
      <div>
        <p className="tw-mb-1 tw-font-display tw-text-sm tw-font-semibold tw-text-content-muted">
          Cost Where It Is Now
        </p>
        <p className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-content">
          {formatCost(fromRow?.bin.unitCost, selectedUnit)}
        </p>
        <p className="tw-mb-0 tw-text-xs tw-text-content-muted">Recorded on the movement for costing history.</p>
      </div>
      <div>
        <p className="tw-mb-1 tw-font-display tw-text-sm tw-font-semibold tw-text-content-muted">
          Cost Once It Arrives
        </p>
        <p className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-content">
          {formatCost(toRow?.bin.unitCost, selectedUnit)}
        </p>
        <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
          The material takes this bin’s cost on arrival. Only the materials controller changes it.
        </p>
      </div>
    </div>
  ) : null;

  const countFields = spec.counted ? (
    <div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
      <div>
        <p id="adjust-method-label" className={LABEL_CLASS}>
          Method
          <Required />
        </p>
        <SegmentedControl
          aria-label="Count method"
          block
          value={form.method}
          onChange={(method) => setField('method', method)}
          options={COUNT_METHOD_OPTIONS}
        />
      </div>
      <Form.Group controlId="adjust-counted" className="tw-mb-0">
        <Form.Label>
          Counted quantity
          <Required />
        </Form.Label>
        <Form.Control
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          value={form.counted}
          isInvalid={!!shown.counted}
          onChange={(event) => setField('counted', event.target.value)}
        />
        {shown.counted ? <Form.Control.Feedback type="invalid">{shown.counted}</Form.Control.Feedback> : unitHint}
      </Form.Group>
      <div>
        <p id="adjust-expected-label" className={LABEL_CLASS}>
          Expected vs. counted
        </p>
        <output
          aria-labelledby="adjust-expected-label"
          className="tw-block tw-min-h-[2.5rem] tw-rounded tw-border tw-border-line-input tw-bg-surface-alt tw-px-3 tw-py-2 tw-text-sm tw-text-content"
        >
          {fromRow ? expectedVsCounted(fromRow.bin.quantity, form.counted, selectedUnit) : '–'}
        </output>
      </div>
    </div>
  ) : null;

  const searchProps = searchMode
    ? {
        query,
        onQueryChange: setQuery,
        loading: searching,
        placeholder:
          type === 'RECEIPT' ? 'Search the catalogue to receive something new here…' : 'Search the catalogue…',
      }
    : undefined;

  return (
    // One height for every type, so the dialog does not jump as the type is switched: the
    // tallest form (Receipt, ~968px with header and footer, measured at 1440px wide) rounded up
    // for a warning, and never more than 90% of the screen, where the body scrolls instead.
    <Modal
      show={show}
      onHide={saving ? () => undefined : onHide}
      size="xl"
      autoHeight
      dialogClassName="sm:tw-min-h-[min(61rem,90vh)]"
    >
      <Modal.Header closeButton>
        <Modal.Title>Adjust inventory</Modal.Title>
        <p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-content-muted">
          Record any stock movement at {site.name}. The type below decides which fields matter.
        </p>
      </Modal.Header>

      <form noValidate onSubmit={handleSave} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body className="tw-space-y-4">
          <SegmentedControl
            aria-label="Movement type"
            block
            value={type}
            onChange={changeType}
            options={MOVEMENT_TABS.map((tab) => ({ value: tab.value, label: tab.label }))}
          />

          {options.status === 'error' ? (
            <Alert variant="danger" className="tw-mb-0">
              <p className="tw-mb-2">{options.message}</p>
              <Button size="sm" variant="outline-danger" onClick={() => loadSupport()}>
                Try again
              </Button>
            </Alert>
          ) : null}

          {spec.fromLabel && spec.toLabel ? (
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
              {fromField}
              {toField}
            </div>
          ) : (
            (fromField ?? toField)
          )}

          <div>
            <p id="adjust-material-label" className={LABEL_CLASS}>
              Material
              <Required />
            </p>
            <MaterialPicker
              labelledBy="adjust-material-label"
              choices={choices}
              value={form.material}
              onChange={chooseMaterial}
              search={searchProps}
              invalid={!!shown.material}
              emptyMessage={
                searchMode
                  ? query.trim().length >= MIN_SEARCH_LENGTH
                    ? searching
                      ? 'Searching…'
                      : 'No catalogue material matches that search.'
                    : 'Nothing is stored here yet. Search the catalogue to receive a material.'
                  : spec.drawsDown
                    ? 'No material with stock is held at this site.'
                    : 'No material is assigned to any location at this site.'
              }
            />
            {shown.material ? <Form.Control.Feedback type="invalid">{shown.material}</Form.Control.Feedback> : null}
          </div>

          {countFields}

          {spec.quantity ? (
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
              {quantityField}
              {taskField ?? vendorField}
            </div>
          ) : null}

          {spec.unitCost ? <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">{costField}</div> : null}

          {costPanel}

          <Form.Group controlId="adjust-reason" className="tw-mb-0">
            <Form.Label>
              Context / reason
              <Required />
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.reason}
              maxLength={1000}
              isInvalid={!!shown.reason}
              onChange={(event) => setField('reason', event.target.value)}
            />
            {shown.reason ? (
              <Form.Control.Feedback type="invalid">{shown.reason}</Form.Control.Feedback>
            ) : (
              <Form.Text>Required for every type – it’s how a manager tells a measurement change from a real move.</Form.Text>
            )}
          </Form.Group>

          {saveError ? (
            <Alert variant="danger" className="tw-mb-0">
              {saveError}
            </Alert>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm">Saving</Spinner>
                Saving…
              </>
            ) : (
              'Save movement'
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AdjustInventoryModal;
